// RITA-1522 (Nutzungserfassung Phase 3d, Epic RITA-1496) — Zeichenfläche und Plaketten.
//
// Portal in document.body, damit das Overlay nicht in einem Container mit `overflow: hidden`,
// `transform` oder eigenem Stapelkontext landet — in der Maske wäre es sonst abgeschnitten.
//
// CONTRACT: Nichts hier fängt Klicks ab (`pointer-events: none` auf beiden Schichten), und beide
// liegen auf `theme.zIndex.appBar + 1`: über der Kopfleiste, aber UNTER Dialog und Drawer. Läge
// das Overlay darüber, hinge jede geöffnete Maske hinter einer nicht bedienbaren Fläche.
//
// CONTRACT: Die Zeichenfläche ist `aria-hidden` — eine Dichte-Wolke ist für eine Vorlesehilfe
// kein Inhalt. Die Zahlen stehen als Plaketten und in der Leiste.
//
// Die Rechenregeln stehen in zeichnen.ts und plaketten.ts und sind dort ohne Canvas geprüft;
// hier bleibt die Anbindung dünn. Neu gezeichnet wird gebündelt über requestAnimationFrame:
// scroll und resize feuern dicht, und nie mehr als ein Frame ist ausstehend.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '@mui/material/styles';
import { useHeatmap } from './HeatmapContext';
import { plakettenZuordnen, PlakettenTreffer } from './plaketten';
import { farbeFuer, HEATMAP_SPALTEN, zellenImFenster, zellenRechteck } from './zeichnen';

/** Eine Plakette an ihrem Platz im Fenster. */
export interface PlakettenPosition {
  /** Eindeutig je Vorkommen: derselbe Schlüssel kann mehrfach auf der Seite stehen. */
  id: string;
  key: string;
  count: number;
  users: number;
  kind: string | null;
  left: number;
  top: number;
}

/** Tooltip einer Plakette. Ohne Elementart bleibt der Zusatz weg statt „(null)“ zu zeigen. */
export const plakettenTitel = (count: number, users: number, kind: string | null): string => {
  const kern = `${count} Klicks, ${users} Personen`;
  return kind ? `${kern} (${kind})` : kern;
};

/**
 * Betrifft mindestens eine der Änderungen etwas AUSSERHALB des eigenen Overlays?
 *
 * Der Beobachter hängt an document.body, und die Plakettenschicht liegt selbst dort: jede
 * gesetzte Plakette wäre eine Änderung am Baum und stieße die nächste Messung an — Frame für
 * Frame, ohne dass sich etwas bewegt. Ohne bekannten eigenen Baum wird lieber gemessen, als
 * eine echte Änderung zu verpassen.
 */
export const betrifftFremdeAenderung = (aenderungen: readonly MutationRecord[], eigenerBaum: Element | null): boolean =>
  aenderungen.some((aenderung) => {
    const ziel = aenderung.target as Node | null;
    if (!eigenerBaum || !ziel) return true;
    return !(eigenerBaum === ziel || eigenerBaum.contains(ziel));
  });

/**
 * Stehen die Plaketten unverändert? Dann bleibt der Zustand, wie er ist — ein neuer Zustand mit
 * gleichem Inhalt hieße Neuzeichnen, und das Neuzeichnen weckte den Beobachter wieder.
 */
export const gleichePositionen = (alt: readonly PlakettenPosition[], neu: readonly PlakettenPosition[]): boolean =>
  alt.length === neu.length &&
  alt.every((eintrag, stelle) => {
    const andere = neu[stelle];
    return (
      eintrag.id === andere.id &&
      eintrag.left === andere.left &&
      eintrag.top === andere.top &&
      eintrag.count === andere.count &&
      eintrag.users === andere.users &&
      eintrag.kind === andere.kind
    );
  });

export default function HeatmapOverlay() {
  const { karte } = useHeatmap();
  const theme = useTheme();
  const ebene = theme.zIndex.appBar + 1;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const schichtRef = useRef<HTMLDivElement | null>(null);
  const [positionen, setPositionen] = useState<PlakettenPosition[]>([]);
  const karteRef = useRef(karte);
  karteRef.current = karte;
  // Die Zuordnung Schlüssel → Element, zwischengespeichert. Beim Scrollen ändern sich nur die
  // Rechtecke; die Suche über das ganze Dokument (querySelectorAll über 16 Selektoren, danach je
  // Kandidat zweimal closest) gehört an Änderungen des Baums, nicht an jeden Frame.
  const trefferRef = useRef<PlakettenTreffer[]>([]);
  const neuZuordnenRef = useRef(true);
  /** Zuletzt gesetzte Maße der Zeichenfläche — 0 heißt „noch nichts gesetzt". */
  const masseRef = useRef({ breite: 0, hoehe: 0, dpr: 0 });

  /**
   * Zeichnet die Wolke für den aktuellen Ausschnitt. Die Zahl der gezeichneten Zellen steht
   * danach als Attribut an der Fläche: sie kommt aus dem Zeichnen selbst und wäre als
   * React-Zustand eine zweite Wahrheit (und ein Zustandswechsel je Scroll-Frame).
   */
  const zeichne = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const breite = Math.max(1, window.innerWidth);
    const hoehe = Math.max(1, window.innerHeight);
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    // Die Fläche hat Fenstergröße; auf einem hochauflösenden Schirm bekommt sie mehr Bildpunkte,
    // sonst sähen die Kreise ausgefranst aus. Geschrieben wird nur bei ÄNDERUNG: jede Zuweisung an
    // canvas.width verwirft den Bildspeicher und legt ihn neu an, und beim Scrollen bleibt die
    // Größe gleich — das clearRect unten genügt.
    const masse = masseRef.current;
    if (masse.breite !== breite || masse.hoehe !== hoehe || masse.dpr !== dpr) {
      masseRef.current = { breite, hoehe, dpr };
      canvas.width = Math.round(breite * dpr);
      canvas.height = Math.round(hoehe * dpr);
      canvas.style.width = `${breite}px`;
      canvas.style.height = `${hoehe}px`;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.setAttribute('data-heatmap-cells', '0');
      return;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, breite, hoehe);

    const aktuelle = karteRef.current;
    const zellen = aktuelle ? aktuelle.cells : [];
    if (zellen.length === 0) {
      canvas.setAttribute('data-heatmap-cells', '0');
      return;
    }
    // Radius = eine Rasterspalte. Die Zahl kommt aus zeichnen.ts (dort gegen das Backend
    // gebunden) und nicht noch einmal als Literal: sie geht zugleich als Rand an zellenImFenster,
    // und ein Auseinanderlaufen ließe die Wolke an der Fensterkante aufpoppen.
    const radius = breite / HEATMAP_SPALTEN;
    const sichtbar = zellenImFenster(zellen, { innerHeight: hoehe, scrollY: window.scrollY, rand: radius });
    const maxCount = zellen.reduce((groesste, zelle) => Math.max(groesste, zelle.count), 0);

    sichtbar.forEach((zelle) => {
      const rechteck = zellenRechteck(zelle, { innerWidth: breite, scrollY: window.scrollY });
      const mitteX = rechteck.left + rechteck.breite / 2;
      const mitteY = rechteck.top + rechteck.hoehe / 2;
      const farbe = farbeFuer(maxCount > 0 ? zelle.count / maxCount : 0);
      const verlauf = ctx.createRadialGradient(mitteX, mitteY, 0, mitteX, mitteY, radius);
      verlauf.addColorStop(0, `rgba(${farbe.r}, ${farbe.g}, ${farbe.b}, ${farbe.deckkraft})`);
      verlauf.addColorStop(1, `rgba(${farbe.r}, ${farbe.g}, ${farbe.b}, 0)`);
      ctx.fillStyle = verlauf;
      ctx.beginPath();
      ctx.arc(mitteX, mitteY, radius, 0, Math.PI * 2);
      ctx.fill();
    });
    canvas.setAttribute('data-heatmap-cells', String(sichtbar.length));
  }, []);

  /**
   * Sucht die Ziele der Plaketten und misst sie aus.
   *
   * CONTRACT: Nur DARGESTELLTE Vorkommen bekommen eine Plakette. Der Bestand hält Bedienelemente
   * im Dokument, die niemand sieht — ein Radio mit `display: none` (components/settings/styles.tsx)
   * und den Navigations-Drawer mit `keepMounted` (NavVertical.tsx), beide voll in
   * INTERACTIVE_SELECTOR. Ein Browser liefert dafür ein Nullrechteck; die Plakette landete damit
   * in der oberen linken Fensterecke und wäre eine falsche Ortsangabe genau an der Stelle, an der
   * eine echte Zahl erwartet wird.
   */
  const messePlaketten = useCallback(() => {
    const aktuelle = karteRef.current;
    if (neuZuordnenRef.current) {
      neuZuordnenRef.current = false;
      trefferRef.current = aktuelle ? plakettenZuordnen(aktuelle.elements, document) : [];
    }
    const gemessen: PlakettenPosition[] = [];
    trefferRef.current.forEach(({ element, ziel }, stelle) => {
      // Abgehängte Knoten aus einem inzwischen neu gerenderten Baum verwerfen — sie liefern ein
      // Nullrechteck und wären damit ohnehin keine Aussage über die Oberfläche.
      if (!ziel.isConnected) return;
      const rechteck = ziel.getBoundingClientRect();
      if (rechteck.width === 0 && rechteck.height === 0) return;
      gemessen.push({
        id: `${element.key}#${String(stelle)}`,
        key: element.key,
        count: element.count,
        users: element.users,
        kind: element.kind,
        // Obere rechte Ecke des Ziels; die Plakette wird per transform darüber geschoben.
        left: rechteck.right,
        top: rechteck.top,
      });
    });
    setPositionen((bisher) => (gleichePositionen(bisher, gemessen) ? bisher : gemessen));
  }, []);

  // Gebündelte Aktualisierung: scroll und resize feuern dicht hintereinander, und jede Messung
  // der Plaketten liest das Layout. Nie mehr als ein Frame steht aus.
  const frameRef = useRef<number | null>(null);
  const aktualisiere = useCallback(() => {
    if (frameRef.current !== null) return;
    const anfordern = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : null;
    if (!anfordern) {
      zeichne();
      messePlaketten();
      return;
    }
    frameRef.current = anfordern(() => {
      frameRef.current = null;
      zeichne();
      messePlaketten();
    });
  }, [zeichne, messePlaketten]);

  /** Scrollen bewegt nur die Rechtecke — die Zuordnung bleibt, wie sie ist. */
  const beiScrollen = useCallback(() => {
    aktualisiere();
  }, [aktualisiere]);

  /** Umbau der Seite (Nachladen, Aufklappen, Größenwechsel): die Zuordnung neu bilden. */
  const beiUmbau = useCallback(() => {
    neuZuordnenRef.current = true;
    aktualisiere();
  }, [aktualisiere]);

  // Erste Darstellung SYNCHRON nach dem Einhängen und nach jeder neuen Antwort: über einen
  // Frame gebündelt wäre die Fläche für einen Wimpernschlag leer — und im Browser-Test wäre
  // nicht unterscheidbar, ob sie leer bleibt. Eine neue Antwort bringt neue Schlüssel, also
  // muss hier immer neu zugeordnet werden.
  useLayoutEffect(() => {
    neuZuordnenRef.current = true;
    zeichne();
    messePlaketten();
  }, [karte, zeichne, messePlaketten]);

  useEffect(() => {
    window.addEventListener('scroll', beiScrollen, { passive: true });
    window.addEventListener('resize', beiUmbau);

    // Der Inhalt einer Maske kommt nach: Tabellen laden, Bereiche klappen auf. Beide Beobachter
    // gibt es nicht überall (jsdom kennt ResizeObserver nicht) — deshalb Feature-Detection.
    const haupt = document.querySelector('main');
    let groesse: ResizeObserver | null = null;
    if (haupt && typeof window.ResizeObserver === 'function') {
      groesse = new window.ResizeObserver(beiUmbau);
      groesse.observe(haupt);
    }
    let baum: MutationObserver | null = null;
    if (typeof window.MutationObserver === 'function') {
      baum = new window.MutationObserver((aenderungen) => {
        if (betrifftFremdeAenderung(aenderungen, schichtRef.current)) beiUmbau();
      });
      baum.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener('scroll', beiScrollen);
      window.removeEventListener('resize', beiUmbau);
      if (groesse) groesse.disconnect();
      if (baum) baum.disconnect();
      if (frameRef.current !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frameRef.current);
      }
      frameRef.current = null;
    };
  }, [beiScrollen, beiUmbau]);

  return ReactDOM.createPortal(
    <>
      <canvas
        ref={canvasRef}
        data-testid="heatmap-canvas"
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: ebene }}
      />
      {/* Wie die Zeichenfläche kein Inhalt für Hilfsmittel: die zugängliche Auswertung steht in der
          Leiste, ein isolierter Zähler ohne Bezug zum Bedienelement wäre im Vorlesebaum nur Rauschen
          (KI-Review !746). */}
      <div
        ref={schichtRef}
        aria-hidden="true"
        style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: ebene }}
      >
        {positionen.map((plakette) => (
          <span
            key={plakette.id}
            data-testid="heatmap-plakette"
            data-key={plakette.key}
            title={plakettenTitel(plakette.count, plakette.users, plakette.kind)}
            style={{
              position: 'absolute',
              left: plakette.left,
              top: plakette.top,
              transform: 'translate(-60%, -50%)',
              padding: '1px 7px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1.5,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              backgroundColor: theme.palette.error.dark,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[3],
            }}
          >
            {plakette.count}
          </span>
        ))}
      </div>
    </>,
    document.body,
  );
}
